import { Flex, Steps, Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { InfoCircleOutlined } from "@/icons"
import { CopyButton } from "@/molecules/CopyButton"
import {
  HowToUseTemplate,
  type HowToUseTemplateProps
} from "@/pages/IntegrationsPage/components/templates/HowToUseTemplate"
import { CODING_AGENTS, type CodingAgent } from "./codingAgents"

import styles from "./HowToUseCodingAgent.module.scss"

export type HowToUseCodingAgentProps = Omit<
  HowToUseTemplateProps,
  "children"
> & {
  agent: string
}

export const HowToUseCodingAgent = ({
  agent,
  ...props
}: HowToUseCodingAgentProps) => {
  const guide = (CODING_AGENTS as Record<string, CodingAgent | undefined>)[
    agent
  ]
  const { t } = useTranslation("translation", {
    keyPrefix: `integrations.${guide?.keyPrefix}.howToUse`
  })
  const { t: tShared } = useTranslation("translation", {
    keyPrefix: "integrations.codingAgent.howToUse"
  })

  if (!guide) return null

  const status = "process" as const
  const items = [
    ...guide.steps.map(({ key, command }) => ({
      status,
      title: t(`${key}.title`),
      content: (
        <div>
          <Text type="secondary">{t(`${key}.description`)}</Text>
          {command && (
            <pre className="code-block-step-how-to-use-template-ks">
              <code>{command}</code>
              <CopyButton text={command} type="link" />
            </pre>
          )}
        </div>
      )
    })),
    {
      status,
      title: t("verify.title"),
      content: (
        <Flex vertical gap={14}>
          <Text type="secondary">{t("verify.description")}</Text>
          <ul className={styles.list}>
            <li>
              <Text type="secondary">{tShared("verify.1")}</Text>
            </li>
            <li>
              <Text type="secondary">{tShared("verify.2")}</Text>
            </li>
            <li>
              <Text type="secondary">{tShared("verify.3")}</Text>
            </li>
          </ul>
          <Flex align="center" gap={8}>
            <Text type="secondary">
              <InfoCircleOutlined />
            </Text>
            <Text type="secondary">{tShared("verify.info")}</Text>
          </Flex>
        </Flex>
      )
    }
  ]

  return (
    <HowToUseTemplate {...props}>
      <Steps size="small" theme="dark" orientation="vertical" items={items} />
    </HowToUseTemplate>
  )
}
